import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useTheme,
    Box,
    Typography,
    IconButton,
    Icon,
    IOption,
    PieChart,
    Pie,
    Cell,
    ChartTooltip,
    ResponsiveContainer,
} from '../../../../base';
import {
    IClientSourceOfHireChartData,
    IChartTooltipProps,
    IPieLabelProps,
    RADIAN,
    SourceOfHireDistributionMetric,
    SOURCE_OF_HIRE_DISTRIBUTION_METRICS,
    SOURCE_OF_HIRE_CHART_COLORS,
    toSourceOfHirePieSlices,
    exportSourceOfHireDistributionToExcel,
} from '../../../../common';
import SelectDropdown from '../../../SelectDropdown';
import {
    StyledPieCard,
    StyledPieCardHeader,
    StyledDropdownSlot,
    StyledPieCardBody,
    StyledPieEmptyState,
    StyledLegend,
    StyledLegendItem,
    StyledColorDot,
} from './PieView.styles';

interface ISourceOfHirePieCardProps {
    distributions: IClientSourceOfHireChartData;
    defaultMetric: SourceOfHireDistributionMetric;
}

const sliceColor = (index: number) =>
    SOURCE_OF_HIRE_CHART_COLORS[index % SOURCE_OF_HIRE_CHART_COLORS.length];

const SourceOfHirePieCard = ({
    distributions,
    defaultMetric,
}: ISourceOfHirePieCardProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [metric, setMetric] =
        useState<SourceOfHireDistributionMetric>(defaultMetric);

    const entries = distributions[metric] ?? [];
    const distribution = toSourceOfHirePieSlices(entries);
    const hasData = distribution.some((slice) => slice.value > 0);

    const metricOptions: IOption[] = SOURCE_OF_HIRE_DISTRIBUTION_METRICS.filter(
        (option) => distributions[option.metric]?.length > 0
    ).map((option) => ({ value: option.metric, label: t(option.labelKey) }));
    const selectedOption =
        metricOptions.find((option) => option.value === metric) ?? null;

    const handleMetricChange = (option: IOption | null) => {
        if (option) {
            setMetric(option.value as SourceOfHireDistributionMetric);
        }
    };

    const renderPercentLabel = ({
        cx,
        cy,
        midAngle,
        outerRadius,
        percent,
    }: IPieLabelProps) => {
        const radius = outerRadius * 0.65;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        if (percent < 0.05) return null;

        return (
            <text
                x={x}
                y={y}
                fill={theme.palette.common.white}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={12}
                fontWeight={600}
            >
                {`${Math.round(percent * 100)}%`}
            </text>
        );
    };

    const renderTooltipContent = ({ active, payload }: IChartTooltipProps) => {
        if (!active || !payload?.length) return null;
        const entry = payload[0];
        return (
            <Box
                sx={{
                    backgroundColor: theme.palette.grey[800],
                    color: theme.palette.common.white,
                    padding: theme.spacing(0.5, 1.5),
                    borderRadius: 1.5,
                }}
            >
                {`${entry.name}: ${Math.round(entry.value)}%`}
            </Box>
        );
    };

    return (
        <StyledPieCard>
            <StyledPieCardHeader direction="row">
                <StyledDropdownSlot>
                    <SelectDropdown
                        options={metricOptions}
                        selected={selectedOption}
                        onChange={handleMetricChange}
                        placeholder={t(
                            'reportsPage.sourceOfHireReport.hiresDistribution'
                        )}
                    />
                </StyledDropdownSlot>
                {hasData && (
                    <IconButton
                        size="small"
                        onClick={() =>
                            exportSourceOfHireDistributionToExcel(
                                entries,
                                metric,
                                t
                            )
                        }
                    >
                        <Icon name="FileDownloadOutlinedIcon" size={16} />
                    </IconButton>
                )}
            </StyledPieCardHeader>
            <StyledPieCardBody direction="row">
                {hasData ? (
                    <>
                        <StyledLegend>
                            {distribution.map((slice, index) => (
                                <StyledLegendItem
                                    direction="row"
                                    key={slice.name}
                                >
                                    <StyledColorDot
                                        dotColor={sliceColor(index)}
                                    />
                                    <Typography variant="body2">
                                        {slice.name}
                                    </Typography>
                                </StyledLegendItem>
                            ))}
                        </StyledLegend>
                        <ResponsiveContainer width={300} height={300}>
                            <PieChart>
                                <Pie
                                    data={distribution}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={22}
                                    outerRadius={140}
                                    paddingAngle={2}
                                    dataKey="value"
                                    stroke={theme.palette.common.white}
                                    strokeWidth={2}
                                    label={renderPercentLabel as never}
                                    labelLine={false}
                                >
                                    {distribution.map((slice, index) => (
                                        <Cell
                                            key={slice.name}
                                            fill={sliceColor(index)}
                                        />
                                    ))}
                                </Pie>
                                <ChartTooltip
                                    content={renderTooltipContent as never}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </>
                ) : (
                    <StyledPieEmptyState>
                        <Icon
                            name="NoTrackableDataIconBig"
                            size={72}
                            color="primary"
                        />
                        <Typography variant="h6">
                            {t(
                                'reportsPage.sourceOfHireReport.noMetricDataTitle'
                            )}
                        </Typography>
                        <Typography
                            variant="body2"
                            color={theme.palette.grey[600]}
                        >
                            {t(
                                'reportsPage.sourceOfHireReport.noMetricDataDescription'
                            )}
                        </Typography>
                    </StyledPieEmptyState>
                )}
            </StyledPieCardBody>
        </StyledPieCard>
    );
};

export default SourceOfHirePieCard;
